// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomerPartNumberField from "@steiner/App/Components/CustomerPartNumbers/CustomerPartNumberField";
import deleteCustomerPartNumber from "@steiner/App/Handlers/CustomerPartNumber/DeleteCustomerPartNumber";
import updateCustomerPartNumberHandler from "@steiner/App/Handlers/CustomerPartNumber/UpdateCutsomerPartNumber";
import { CustomerProductModel } from "@steiner/App/Store/Reducers";
import { xTranslate } from "@steiner/XDL/Utilities/translate";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    customerProduct: CustomerProductModel;
    showAddToCart?: boolean;
    showAddToList?: boolean;
    labels: { [key: string]: string };
}

const mapDispatchToProps = {
    updateCustomerPartNumberHandler,
    deleteCustomerPartNumber,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasToasterContext;

interface CustomerPartNumberItemWithoutProductStyles {
    productDescriptionCell?: DataTableCellProps;
    partNumberCell?: DataTableCellProps;
    editCell?: DataTableCellProps;
    deleteCell?: DataTableCellProps;
    addToCartCell?: DataTableCellProps;
    addToListCell?: DataTableCellProps;
    actionsContainer?: InjectableCss;
    updatePartNumberButton?: ButtonPresentationProps;
    productDescriptionText?: TypographyPresentationProps;
    modalGridContainer?: GridContainerProps;
    modalGridItem?: GridItemProps;
    modalProductDescriptionLinkStyle?: LinkPresentationProps;
    modalErpNumberLinkStyle?: LinkPresentationProps;
    modalCancelButton?: GridItemProps;
    modalConfirmButton?: GridItemProps;
    editLink?: LinkPresentationProps;
    deleteLink?: LinkPresentationProps;
    productDescriptionWrapper?: InjectableCss;
}

export const customerPartNumberItemWithoutProductStyles: CustomerPartNumberItemWithoutProductStyles = {};

const styles = customerPartNumberItemWithoutProductStyles;

const CustomerPartNumberItemWithoutProduct: FC<Props> = ({
    customerProduct,
    labels,
    toaster,
    showAddToCart = true,
    showAddToList = true,
    updateCustomerPartNumberHandler,
    deleteCustomerPartNumber,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeProduct, setActiveProduct] = useState({} as CustomerProductModel);
    const [editingState, setEditingState] = useState<Record<string, boolean>>({});
    const [newPartNumber, setNewPartNumber] = useState("");
    const partNumberRef = useRef<HTMLInputElement>(null);
    const editOrUpdateRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (activeProduct && editingState[`${activeProduct.customerPartNumber}${activeProduct.productErpNumber}`]) {
            partNumberRef.current?.focus();
        }
        // Event listener
        const closeEditMode = (event: MouseEvent) => {
            if (
                partNumberRef.current &&
                editOrUpdateRef.current &&
                !partNumberRef.current.contains(event.target as Node) &&
                !editOrUpdateRef.current.contains(event.target as Node)
            ) {
                if (activeProduct.customerPartNumber && activeProduct.productErpNumber) {
                    setCustomerProductEditingState(
                        activeProduct.customerPartNumber,
                        activeProduct.productErpNumber,
                        false,
                    );
                }
            }
        };
        document.body.addEventListener("mousedown", closeEditMode);
        return () => {
            document.body.removeEventListener("mousedown", closeEditMode);
        };
    }, [activeProduct, editingState]);

    const setCustomerProductEditingState = (partNumber: string, erpNumber: string, state: boolean) => {
        setEditingState(prevState => {
            return {
                ...prevState,
                [`${partNumber}${erpNumber}`]: state,
            };
        });
    };

    const successUpdateHandler = (newPartNumber: string, erpNumber: string) => {
        toaster.addToast({
            body: translate("Customer Part Number Updated Successfully."),
            messageType: "success",
        });
        setCustomerProductEditingState(newPartNumber, erpNumber, false);
    };

    const errorUpdateHandler = (errorMessage: string, customerPartNumber: string, erpNumber: string) => {
        toaster.addToast({
            body: errorMessage,
            messageType: "danger",
        });

        setCustomerProductEditingState(customerPartNumber, erpNumber, false);
    };

    const updateCustomerPartNumber = (customerProduct: CustomerProductModel) => {
        if (customerProduct.customerPartNumber === newPartNumber) {
            toaster.addToast({
                body: translate("Provided part number is the same as the previous part number."),
                messageType: "warning",
            });
            if (customerProduct.productErpNumber) {
                setCustomerProductEditingState(
                    customerProduct.customerPartNumber,
                    customerProduct.productErpNumber,
                    false,
                );
            }
        } else {
            updateCustomerPartNumberHandler({
                customerPartNumber: newPartNumber,
                productNumber: activeProduct.productErpNumber,
                onSuccess: () => {
                    activeProduct.productErpNumber &&
                        successUpdateHandler(newPartNumber, activeProduct.productErpNumber);
                },
                onError: (errorMessage: string) => {
                    if (customerProduct.customerPartNumber && customerProduct.productErpNumber) {
                        errorUpdateHandler(
                            errorMessage,
                            customerProduct.customerPartNumber,
                            customerProduct.productErpNumber,
                        );
                    }
                },
            } as any);
        }
    };

    const deleteCustomerPartNumberHandler = () => {
        deleteCustomerPartNumber({
            customerPartNumber: activeProduct.customerPartNumber,
            productNumber: activeProduct.productErpNumber,
            onSuccess: () =>
                toaster.addToast({
                    body: translate("Customer Part Number Deleted Successfully."),
                    messageType: "success",
                }),
            onError: () =>
                toaster.addToast({
                    body: translate("An error occurred while attempting to delete the Customer Part Number."),
                    messageType: "danger",
                }),
        } as any);

        setIsOpen(false);
    };

    const editClickHandler = (customerProduct: CustomerProductModel) => {
        setActiveProduct(customerProduct);
        setNewPartNumber(customerProduct.customerPartNumber!);
        setEditingState(prevState => {
            const newState: Record<string, boolean> = {};
            Object.keys(prevState).forEach(key => {
                newState[key] = false;
            });
            newState[`${customerProduct.customerPartNumber}${customerProduct.productErpNumber}`] = true;
            return newState;
        });
    };

    const handleUpdateOnKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
        customerProduct: CustomerProductModel,
    ) => {
        if (event.key === "Enter") {
            event.preventDefault();
            updateCustomerPartNumber(customerProduct);
        }
    };

    const isEditing = editingState[`${customerProduct.customerPartNumber}${customerProduct.productErpNumber}`];
    const isDisabled = !customerProduct.isVisible;

    return (
        <DataTableRow key={`${customerProduct.customerPartNumber}${customerProduct.productErpNumber}`}>
            <DataTableCell {...{ header: labels.productDescription }} {...styles.productDescriptionCell}>
                <StyledWrapper {...styles.productDescriptionWrapper}>
                    <Link
                        onClick={() => {
                            setIsOpen(true);
                            setActiveProduct(customerProduct);
                        }}
                        {...styles.deleteLink}
                    ></Link>
                    <Typography {...styles.productDescriptionText}>
                        {customerProduct.productDescription || translate("Product not found")}
                    </Typography>
                </StyledWrapper>
            </DataTableCell>
            <DataTableCell
                {...styles.partNumberCell}
                {...{ header: labels.yourPartNumber }}
                onClick={() => {
                    !isDisabled && editClickHandler(customerProduct);
                }}
            >
                <CustomerPartNumberField
                    isEditing={isEditing && !isDisabled}
                    customerPartNumber={customerProduct.customerPartNumber}
                    textFieldProps={{
                        placeholder: translate("Enter a new part number"),
                        value: newPartNumber,
                        ref:
                            customerProduct.customerPartNumber === activeProduct.customerPartNumber
                                ? partNumberRef
                                : null,
                        required: true,
                        error: !newPartNumber ? translate("Part Number can't be empty") : "",
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                            setNewPartNumber(e.target.value);
                        },
                        onKeyDown: event => {
                            handleUpdateOnKeyDown(event as any, customerProduct);
                        },
                        "data-test-selector": "customerPartNumber",
                        disabled: isDisabled,
                    }}
                />
            </DataTableCell>
            <DataTableCell {...{ header: labels.edit }} {...styles.editCell}>
                <StyledWrapper
                    ref={
                        customerProduct.customerPartNumber === activeProduct.customerPartNumber ? editOrUpdateRef : null
                    }
                >
                    <Link
                        {...styles.editLink}
                        disabled={isDisabled}
                        id="edit"
                        onClick={() => {
                            !isDisabled && editClickHandler(customerProduct);
                        }}
                    >
                        {translate("Edit")}
                    </Link>
                </StyledWrapper>
            </DataTableCell>
            {showAddToCart && <DataTableCell {...styles.addToCartCell} {...{ header: labels.addToCart }} />}
            {showAddToList && <DataTableCell {...styles.addToListCell} {...{ header: labels.addToList }} />}
            <Modal
                sizeVariant="small"
                isOpen={isOpen}
                handleClose={() => {
                    setIsOpen(false);
                }}
                headline={translate("Deletion Confirmation")}
            >
                <GridContainer {...styles.modalGridContainer}>
                    <GridItem {...styles.modalGridItem}>
                        <Typography>
                            {xTranslate(
                                "Are you sure you want to delete the customer part number for product with description: {0} and with ERP number: {1} ",
                                <Link href={activeProduct.productUri} {...styles.modalProductDescriptionLinkStyle}>
                                    {activeProduct.productDescription}
                                </Link>,
                                <Link href={activeProduct.productUri} {...styles.modalErpNumberLinkStyle}>
                                    {activeProduct.productErpNumber}
                                </Link>,
                            )}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.modalCancelButton}>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setIsOpen(false);
                            }}
                        >
                            {translate("Cancel")}
                        </Button>
                    </GridItem>
                    <GridItem {...styles.modalConfirmButton}>
                        <Button variant="primary" onClick={deleteCustomerPartNumberHandler}>
                            {translate("Confirm")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Modal>
        </DataTableRow>
    );
};

export default connect(null, mapDispatchToProps)(withToaster(CustomerPartNumberItemWithoutProduct));
